// src/components/pages/EventManager/EventDetailPage.js
// Ensures authoritative Firestore fetch, robust date normalization, and no early "not available" flash.
// Also defers PayPal SDK load and uses rentalDurationDays.

import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Snackbar,
  Alert,
  Typography,
  Card,
  CardMedia,
  CardContent,
} from "@mui/material";
import { useTheme } from "styled-components";
import LocalMoviesOutlinedIcon from "@mui/icons-material/LocalMoviesOutlined";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../../firebase";
import {
  PayPalScriptProvider,
  PayPalButtons,
  usePayPalScriptReducer,
} from "@paypal/react-paypal-js";
import {
  addUserRental,
  getUserRental,
  isRentalActive,
} from "../../../services/rentalService";
import { fetchEventVisuals } from "../../../services/eventVisualService";

// ---------- utils ----------
const currency = "USD";
const fmtPrice = (n) =>
  typeof n === "number"
    ? new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: 2,
    }).format(n)
    : "";

// Normalize Firestore Timestamp | number (ms) | ISO string -> Date
const normalizeDate = (v) => {
  if (!v) return null;
  if (typeof v?.toDate === "function") {
    const d = v.toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === "number") {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

// Human-friendly range like:
// same month/year:  "Dec 4–31, 2025"
// same year:        "Dec 4 – Jul 31, 2025"
// different years:  "Dec 4, 2025 – Jul 31, 2026"
const formatHumanDateRange = (start, end, tz = "America/New_York") => {
  if (!start || !end) return "";

  const fmtMonth = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: tz });
  const fmtDay = new Intl.DateTimeFormat("en-US", { day: "numeric", timeZone: tz });
  const fmtYear = new Intl.DateTimeFormat("en-US", { year: "numeric", timeZone: tz });

  const sMonth = fmtMonth.format(start);
  const sDay = fmtDay.format(start);
  const sYear = fmtYear.format(start);

  const eMonth = fmtMonth.format(end);
  const eDay = fmtDay.format(end);
  const eYear = fmtYear.format(end);

  if (sYear === eYear) {
    if (sMonth === eMonth) {
      // Dec 4–31, 2025
      return `${sMonth} ${sDay}–${eDay}, ${sYear}`;
    }
    // Dec 4 – Jul 31, 2025
    return `${sMonth} ${sDay} – ${eMonth} ${eDay}, ${sYear}`;
  }
  // Dec 4, 2025 – Jul 31, 2026
  return `${sMonth} ${sDay}, ${sYear} – ${eMonth} ${eDay}, ${eYear}`;
};


// ---------- PayPal (deferred SDK load) ----------
function PayPalSection({ event, onApprove, onError }) {
  const [{ isPending }, dispatch] = usePayPalScriptReducer();

  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";
  const price = Number(event?.price ?? 0);

  const desiredOptions = React.useMemo(
    () => ({
      "client-id": clientId,
      currency,
      intent: "capture",
      components: "buttons",
      "data-sdk-integration-source": "react-paypal-js",
    }),
    [clientId]
  );

  React.useEffect(() => {
    if (!clientId || !(price > 0)) return;
    dispatch({ type: "resetOptions", value: desiredOptions });
    dispatch({ type: "setLoadingStatus", value: "pending" });
  }, [clientId, price, desiredOptions, dispatch]);

  React.useEffect(() => {
    const scripts = document.querySelectorAll('script[src*="paypal.com/sdk/js"]');
    if (scripts.length > 1) {
      // eslint-disable-next-line no-console
      console.warn("[PayPal] Multiple SDK scripts detected:", scripts);
    }
  }, []);

  if (!clientId) {
    return (
      <Alert severity="error" sx={{ mb: 1 }}>
        PayPal is not configured. Missing client id.
      </Alert>
    );
  }
  if (!(price > 0)) {
    return (
      <Alert severity="warning" sx={{ mb: 1 }}>
        This item is not purchasable right now.
      </Alert>
    );
  }

  return (
    <Box sx={{ position: "relative" }}>
      {isPending && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <CircularProgress size={22} />
        </Box>
      )}
      <Box id="paypal-container" sx={{ minHeight: 46 }}>
        <PayPalButtons
          key={`${event?.id || "event"}-${price}`}
          forceReRender={[event?.id, price, currency]}
          style={{ layout: "vertical", shape: "rect", label: "paypal" }}
          createOrder={(data, actions) =>
            actions.order.create({
              purchase_units: [
                {
                  description: `${event?.title || "Event"} rental`,
                  amount: { currency_code: currency, value: price.toFixed(2) },
                },
              ],
              application_context: { shipping_preference: "NO_SHIPPING" },
            })
          }
          onApprove={onApprove}
          onError={onError}
        />
      </Box>
    </Box>
  );
}

// ---------- Page ----------
const EventDetailPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  // state
  const [user, setUser] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  // Use router state for initial paint (may be skeletal)
  const [event, setEvent] = React.useState(location.state?.event || null);
  const [thumb, setThumb] = React.useState(null);
  const [teaser, setTeaser] = React.useState(null);
  const [contentThumbs, setContentThumbs] = React.useState([]);

  const [rental, setRental] = React.useState(null);
  const [hasActiveRental, setHasActiveRental] = React.useState(false);

  const [snack, setSnack] = React.useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const openSnack = (msg, severity = "success") =>
    setSnack({ open: true, msg, severity });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // auth
  React.useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, []);

  // Always fetch authoritative Firestore doc; merge into any router state.
  React.useEffect(() => {
    let canceled = false;

    const load = async () => {
      if (!eventId) return;
      setLoading(true);
      try {
        // Optimistic: if we had router state, keep it for the first paint
        // Authoritative: fetch from Firestore regardless
        const snap = await getDoc(doc(db, "events", eventId));
        if (!snap.exists()) {
          openSnack("Event not found.", "error");
          return;
        }
        const fresh = { id: snap.id, ...snap.data() };
        if (canceled) return;

        // Merge to avoid flicker (router state may have display-only props)
        setEvent((prev) => ({ ...(prev || {}), ...fresh }));

        const visuals = await fetchEventVisuals(fresh, {
          maxThumbs: 6,
          preferEventThumbnail: true,
        });
        if (canceled) return;

        setThumb(visuals.thumbnailUrl);
        setTeaser(visuals.teaserUrl);
        setContentThumbs(visuals.contentThumbs);
      } catch (e) {
        openSnack(e?.message || "Failed to load event.", "error");
      } finally {
        if (!canceled) setLoading(false);
      }
    };

    load();
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  // rental status
  React.useEffect(() => {
    const checkRental = async () => {
      if (!user?.uid || !event?.id) return;
      const r = await getUserRental(user.uid, event.id);
      setRental(r);
      setHasActiveRental(isRentalActive(r));
    };
    checkRental();
  }, [user, event]);

  // derived schedule
  const start = React.useMemo(() => normalizeDate(event?.startDate), [event?.startDate]);
  const end = React.useMemo(() => normalizeDate(event?.endDate), [event?.endDate]);
  const scheduleReady = Boolean(start && end);

  const canRentNow = React.useMemo(() => {
    if (!scheduleReady) return false;
    const now = new Date();
    return start <= now && end >= now;
  }, [scheduleReady, start, end]);

  // rental days (your field name is rentalDurationDays)
  const rentalDays = React.useMemo(() => {
    const v =
      typeof event?.rentalDurationDays === "number"
        ? event.rentalDurationDays
        : typeof event?.rentalDays === "number"
          ? event.rentalDays
          : null;
    return v && v > 0 ? v : null;
  }, [event?.rentalDurationDays, event?.rentalDays]);

  // nav
  const goBack = () => navigate(-1);
  const goToSignInPreservingReturn = () => {
    const returnTo =
      location.pathname + (location.search || "") + (location.hash || "");
    if (/^\/challenge\/events\//.test(location.pathname)) {
      sessionStorage.setItem("returnToAfterAuth", returnTo);
    }
    navigate("/challenge/signin", {
      state: { from: { pathname: returnTo } },
    });
  };

  // PayPal handlers
  const onApprove = async (data, actions) => {
    try {
      const details = await actions.order.capture();
      if (!user) {
        openSnack("Please sign in to complete your rental.", "error");
        return;
      }
      await addUserRental({ uid: user.uid, event, capture: details, currency });
      openSnack("Success! Your rental is active.", "success");
      const r = await getUserRental(user.uid, event.id);
      setRental(r);
      setHasActiveRental(isRentalActive(r));
      navigate(`/challenge/events/${event.id}/watch`, { state: { event } });
    } catch (e) {
      openSnack(e?.message || "Payment could not be captured.", "error");
    }
  };

  const onError = (err) => {
    // eslint-disable-next-line no-console
    console.error("[PayPal] error:", err);
    openSnack("Payment failed. Please try again.", "error");
  };

  // early returns after hooks
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "40vh" }}>
          <CircularProgress sx={{ color: theme.colors.red }} />
        </Box>
      </Container>
    );
  }

  if (!event) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h6" color="error">
          Event not found.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Hero */}
      <Box
        sx={{
          position: "relative",
          borderRadius: "24px",
          overflow: "hidden",
          minHeight: { xs: 220, sm: 300, md: 360 },
          border: `1px solid ${theme.colors.palePurple}`,
        }}
      >
        {/* Background image layer (filter only here) */}
        {thumb ? (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `url(${thumb}) center/cover no-repeat`,
              filter: "brightness(0.7)",     // <— dims only the background
              zIndex: 0,
            }}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(135deg, ${theme.colors.darkBlue} 0%, ${theme.colors.mediumPurple} 60%, ${theme.colors.headerPurple} 100%)`,
              zIndex: 0,
            }}
          />
        )}

        {/* Gradient overlay (optional, to taste) */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.30) 0%, rgba(0,0,0,0.50) 35%, rgba(0,0,0,0.70) 100%)",
            zIndex: 1,
          }}
        />

        {/* Content (pure white text, unaffected by filters) */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            color: "#fff",                 // ensure full white
            p: { xs: 3, sm: 4, md: 5 },
            fontFamily: "basic-sans",
          }}
        >
          <Button
            onClick={goBack}
            variant="outlined"
            sx={{
              mb: 2,
              textTransform: "none",
              color: theme.colors.amber,
              borderColor: theme.colors.amber,
              borderRadius: "10px",
              "&:hover": { color: theme.colors.green, borderColor: theme.colors.green },
            }}
          >
            Back
          </Button>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!thumb && <LocalMoviesOutlinedIcon sx={{ fontSize: 28, color: theme.colors.amber }} />}
            <Typography
              variant="h3"
              sx={{ color: "#fff", fontWeight: 800, fontSize: { xs: "1.9rem", sm: "2.3rem", md: "2.7rem" } }}
            >
              {event.title || "Event"}
            </Typography>
          </Box>

          {event.description && (
            <Typography variant="body1" sx={{ mt: 1.5, maxWidth: 900, color: "#fff" }}>
              {event.description}
            </Typography>
          )}

          <Box sx={{ display: "flex", gap: 1.5, mt: 2, flexWrap: "wrap" }}>
            {typeof event.price === "number" && (
              <Chip
                label={fmtPrice(event.price)}
                sx={{
                  bgcolor: "rgba(0,0,0,0.35)",
                  border: `1px solid ${theme.colors.palePurple}`,
                  color: "#fff",
                  fontWeight: 700,
                  "& .MuiChip-label": { color: "#fff", fontWeight: 700 }, // ensure pill text is pure white
                }}
              />
            )}

            {rentalDays && (
              <Chip
                label={`${rentalDays} day${rentalDays !== 1 ? "s" : ""} access`}
                sx={{
                  bgcolor: "rgba(0,0,0,0.35)",
                  border: `1px solid ${theme.colors.palePurple}`,
                  color: "#fff",
                  "& .MuiChip-label": { color: "#fff", fontWeight: 600 },
                }}
              />
            )}

            {start && end && (
              <Chip
                label={`Available to rent from ${formatHumanDateRange(start, end)}`}
                sx={{
                  bgcolor: "rgba(0,0,0,0.35)",
                  border: `1px solid ${theme.colors.palePurple}`,
                  color: "#fff",
                  "& .MuiChip-label": { color: "#fff" },
                }}
              />
            )}

            {hasActiveRental && rental?.accessEnd && (
              <Chip
                label={`Access until ${rental.accessEnd?.toDate
                    ? rental.accessEnd.toDate().toLocaleString()
                    : new Date(rental.accessEnd).toLocaleString()
                  }`}
                sx={{
                  bgcolor: "rgba(0,0,0,0.35)",
                  border: `1px solid ${theme.colors.palePurple}`,
                  color: "#fff",
                  "& .MuiChip-label": { color: "#fff" },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>


      {/* Body */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Left: teaser / content */}
        <Grid item xs={12} md={8}>
          {teaser ? (
            <Card
              sx={{
                backgroundColor: theme.colors.mediumPurple,
                color: "white",
                borderRadius: "20px",
                border: `1px solid ${theme.colors.palePurple}`,
                overflow: "hidden",
              }}
            >
              <CardMedia component="video" src={teaser} controls preload="metadata" />
              <CardContent>
                <Typography variant="h6" sx={{ fontFamily: "basic-sans" }}>
                  Sneak peek
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  A short teaser of the event.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Card
              sx={{
                backgroundColor: theme.colors.mediumPurple,
                color: "white",
                borderRadius: "20px",
                border: `1px solid ${theme.colors.palePurple}`,
                p: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontFamily: "basic-sans" }}>
                Sneak peek
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Teaser not available for this event.
              </Typography>
            </Card>
          )}

          {contentThumbs.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" sx={{ mb: 1, fontFamily: "basic-sans", color: "white" }}>
                What’s included
              </Typography>
              <Grid container spacing={1.5}>
                {contentThumbs.map((t, i) => (
                  <Grid item xs={6} sm={4} md={4} key={`${t}-${i}`}>
                    <Box
                      sx={{
                        borderRadius: "12px",
                        overflow: "hidden",
                        border: `1px solid ${theme.colors.palePurple}`,
                        aspectRatio: "16 / 9",
                        background: `url(${t}) center/cover no-repeat`,
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Grid>

        {/* Right: purchase / access panel */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              backgroundColor: theme.colors.mediumPurple,
              color: "white",
              borderRadius: "20px",
              border: `1px solid ${theme.colors.palePurple}`,
            }}
          >
            <CardContent>
              {!user ? (
                <>
                  <Typography variant="h6" sx={{ fontFamily: "basic-sans", mb: 1 }}>
                    Rent this event
                  </Typography>
                  <Button
                    fullWidth
                    onClick={goToSignInPreservingReturn}
                    variant="contained"
                    sx={{
                      mb: 1,
                      backgroundColor: theme.colors.blue,
                      color: theme.colors.white,
                      borderRadius: "50px",
                      padding: "10px 30px",
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": { backgroundColor: theme.colors.green, color: theme.colors.white, boxShadow: "none" },
                    }}
                  >
                    Sign in to rent
                  </Button>
                </>
              ) : hasActiveRental ? (
                <>
                  <Typography variant="h6" sx={{ fontFamily: "basic-sans", mb: 1 }}>
                    You have access
                  </Typography>
                  <Button
                    fullWidth
                    onClick={() =>
                      navigate(`/challenge/events/${event.id}/watch`, { state: { event } })
                    }
                    variant="contained"
                    sx={{
                      mb: 1,
                      backgroundColor: theme.colors.blue,
                      color: theme.colors.white,
                      borderRadius: "50px",
                      padding: "10px 30px",
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": { backgroundColor: theme.colors.green, color: theme.colors.white, boxShadow: "none" },
                    }}
                  >
                    Watch now
                  </Button>
                </>
              ) : !scheduleReady ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Checking availability…</Typography>
                </Box>
              ) : canRentNow ? (
                <>
                  <Typography variant="h6" sx={{ fontFamily: "basic-sans", mb: 1 }}>
                    Rent this event
                  </Typography>
                  {typeof event.price === "number" && (
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                      {fmtPrice(event.price)}
                    </Typography>
                  )}
                  {rentalDays && (
                    <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
                      {rentalDays} day{rentalDays !== 1 ? "s" : ""} access starts right after purchase.
                    </Typography>
                  )}
                  <PayPalScriptProvider deferLoading={true}>
                    <PayPalSection event={event} onApprove={onApprove} onError={onError} />
                  </PayPalScriptProvider>
                </>
              ) : (
                <Alert severity="warning">This event is not available to rent right now.</Alert>
              )}

              <Typography variant="caption" sx={{ display: "block", mt: 2, opacity: 0.7 }}>
                Payments are processed by PayPal. We do not store your card details.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeSnack} severity={snack.severity} variant="filled" elevation={3}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EventDetailPage;
